import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/client'
import { GET_CURRENT_USER } from '../graphql/queries'
import { UPDATE_MY_PROFILE, CHANGE_MY_PASSWORD } from '../graphql/mutations'
import { auth } from '../lib/auth'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'

const ProfilePage = () => {
  const navigate = useNavigate()

  const { data, loading, refetch } = useQuery(GET_CURRENT_USER)
  const me = data?.me

  const [name, setName] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  useEffect(() => {
    if (me) {
      setName(me.name || '')
      setPhoneNumber(me.phone_number || '')
    }
  }, [me])

  const [updateProfile, { loading: savingProfile }] = useMutation(UPDATE_MY_PROFILE, {
    onCompleted: (result) => {
      toast.success('Profile updated')
      // Keep the cached localStorage user in sync (Layout shows the name)
      const stored = auth.getUser()
      if (stored) {
        localStorage.setItem('user', JSON.stringify({ ...stored, name: result.updateMyProfile.name }))
      }
      refetch()
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update profile')
    },
  })

  const [changePassword, { loading: changingPassword }] = useMutation(CHANGE_MY_PASSWORD, {
    onCompleted: () => {
      // All sessions are logged out server-side (refresh tokens revoked)
      toast.success('Password changed — please sign in again')
      auth.clearAuth()
      window.dispatchEvent(new Event('storage'))
      navigate('/login', { replace: true })
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to change password')
    },
  })

  const handleProfileSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) {
      toast.error('Name cannot be empty')
      return
    }
    updateProfile({ variables: { name: name.trim(), phone_number: phoneNumber.trim() } })
  }

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!currentPassword) {
      toast.error('Please enter your current password')
      return
    }
    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters long')
      return
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match')
      return
    }
    changePassword({ variables: { currentPassword, newPassword } })
  }

  if (loading && !me) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">My Profile</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage your personal information and password
        </p>
      </div>

      {/* Profile details */}
      <div className="card">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Personal information</h2>
        <form onSubmit={handleProfileSubmit} className="space-y-4">
          <div>
            <label htmlFor="profile-email" className="block text-sm font-medium text-gray-700 mb-1">
              Email address
            </label>
            <input
              type="email"
              id="profile-email"
              className="input-field bg-gray-50 text-gray-500 cursor-not-allowed"
              value={me?.email || ''}
              disabled
            />
            <p className="mt-1 text-xs text-gray-500">Your email address is managed by your administrator.</p>
          </div>

          <div>
            <label htmlFor="profile-name" className="block text-sm font-medium text-gray-700 mb-1">
              Name
            </label>
            <input
              type="text"
              id="profile-name"
              className="input-field"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div>
            <label htmlFor="profile-phone" className="block text-sm font-medium text-gray-700 mb-1">
              Phone number
            </label>
            <input
              type="tel"
              id="profile-phone"
              className="input-field"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
            />
          </div>

          <div className="flex justify-end">
            <button type="submit" className="btn-primary" disabled={savingProfile}>
              {savingProfile ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>

      {/* Change password */}
      <div className="card">
        <h2 className="text-lg font-medium text-gray-900 mb-1">Change password</h2>
        <p className="text-sm text-gray-500 mb-4">
          After changing your password you'll be signed out everywhere and need to sign in again.
        </p>
        <form onSubmit={handlePasswordSubmit} className="space-y-4">
          <div>
            <label htmlFor="current-password" className="block text-sm font-medium text-gray-700 mb-1">
              Current password
            </label>
            <input
              type="password"
              id="current-password"
              className="input-field"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
          </div>

          <div>
            <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-1">
              New password
            </label>
            <input
              type="password"
              id="new-password"
              className="input-field"
              autoComplete="new-password"
              placeholder="Min. 6 characters"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </div>

          <div>
            <label htmlFor="confirm-new-password" className="block text-sm font-medium text-gray-700 mb-1">
              Confirm new password
            </label>
            <input
              type="password"
              id="confirm-new-password"
              className="input-field"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>

          <div className="flex justify-end">
            <button type="submit" className="btn-primary" disabled={changingPassword}>
              {changingPassword ? 'Changing…' : 'Change password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ProfilePage
